import { PagingParameters } from "@/api/dtos";
import {
  useItinerantGroupStops,
  useListItinerantExhibitions,
} from "@/api/related-fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sortable,
  SortableDragHandle,
  SortableItem,
} from "@/components/ui/sortable";
import {
  Exhibition,
  ItinerantExhibitionParentType,
} from "@/lib/types";
import { cn } from "@/lib/utils";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type ItinerantStopDraft = {
  id: string;
  linkId?: string;
  exhibition: Exhibition;
  catalogNumber: string;
  exemplaireExpose: string;
  note: string;
};

/** Default list order = by year (oldest first); numero as tiebreaker. */
function parseExhibitionYear(exhibition: Exhibition): number {
  const year = parseInt(exhibition.expositionsDescriptionAnnee || "", 10);
  if (!Number.isNaN(year)) return year;
  const dates = exhibition.expositionsDescriptionDates || "";
  const match = dates.match(/\b(19|20)\d{2}\b/);
  if (match) return parseInt(match[0], 10);
  return Number.MAX_SAFE_INTEGER;
}

function sortByDateDefault(a: Exhibition, b: Exhibition) {
  const ya = parseExhibitionYear(a);
  const yb = parseExhibitionYear(b);
  if (ya !== yb) return ya - yb;
  return (a.expositionsDescriptionNumeroExpo || "").localeCompare(
    b.expositionsDescriptionNumeroExpo || "",
    undefined,
    { numeric: true },
  );
}

export function itinerantStopFromExhibition(
  exhibition: Exhibition,
  linkId?: string,
  extras?: Partial<
    Pick<ItinerantStopDraft, "catalogNumber" | "exemplaireExpose" | "note">
  >,
): ItinerantStopDraft {
  return {
    id: linkId || exhibition.id,
    linkId,
    exhibition,
    catalogNumber: extras?.catalogNumber ?? "",
    exemplaireExpose: extras?.exemplaireExpose ?? "",
    note: extras?.note ?? "",
  };
}

type ItinerantExhibitionPanelProps = {
  open: boolean;
  seedExhibition?: Exhibition;
  initialStops?: ItinerantStopDraft[];
  /** When set, loads the full group from API (edit flow). */
  groupId?: string | null;
  parentType?: ItinerantExhibitionParentType;
  parentId?: string;
  stops: ItinerantStopDraft[];
  onStopsChange: (stops: ItinerantStopDraft[]) => void;
  /** When false, stop cards are list-only; edit fields live on the right form. */
  inlineFields?: boolean;
  showCatalogNumber?: boolean;
  showExemplaireExpose?: boolean;
  selectedStopId?: string | null;
  onSelectStop?: (stopId: string) => void;
};

/**
 * Left-side panel for itinerant exhibition management (stops + catalog of
 * itinerant expos). Used inside the existing create/edit link drawer.
 */
export function ItinerantExhibitionPanel({
  open,
  seedExhibition,
  initialStops,
  groupId,
  parentType,
  parentId,
  stops,
  onStopsChange,
  inlineFields = true,
  showCatalogNumber = true,
  showExemplaireExpose = false,
  selectedStopId,
  onSelectStop,
}: ItinerantExhibitionPanelProps) {
  const { t } = useTranslation();
  const vt = useVisibleT();
  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 20,
  });

  const { data: itinerantList } = useListItinerantExhibitions(
    pagination,
    search,
    open,
  );

  const { data: fetchedGroupStops } = useItinerantGroupStops(
    groupId,
    parentType,
    parentId,
    Boolean(open && groupId),
  );

  const resolvedInitialStops = useMemo(() => {
    if (fetchedGroupStops && fetchedGroupStops.length > 0) {
      return fetchedGroupStops.map((s) =>
        itinerantStopFromExhibition(s.exhibition, s.linkId, {
          catalogNumber: s.catalogNumber,
          exemplaireExpose: s.exemplaireExpose,
          note: s.note,
        }),
      );
    }
    return initialStops;
  }, [fetchedGroupStops, initialStops]);

  // Seed once per open — never downgrade a full group to a single seed
  const seededForOpenRef = useRef<string | null>(null);
  /** Once the user drag-reorders, keep list order (no date re-sort on add). */
  const userReorderedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededForOpenRef.current = null;
      userReorderedRef.current = false;
      return;
    }

    if (resolvedInitialStops && resolvedInitialStops.length > 0) {
      const key = `group:${resolvedInitialStops.map((s) => s.id).join(",")}`;
      if (seededForOpenRef.current === key) return;
      seededForOpenRef.current = key;
      // Editing an existing group: trust saved list order (itinerantOrder)
      userReorderedRef.current = true;
      onStopsChange(resolvedInitialStops);
      return;
    }

    // Still waiting for group fetch
    if (groupId && fetchedGroupStops === undefined) return;

    if (seedExhibition) {
      // Do not replace an already-seeded group with a lone seed
      if (seededForOpenRef.current?.startsWith("group:")) return;
      const key = `seed:${seedExhibition.id}`;
      if (seededForOpenRef.current === key) return;
      seededForOpenRef.current = key;
      onStopsChange([itinerantStopFromExhibition(seedExhibition)]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    seedExhibition?.id,
    resolvedInitialStops,
    groupId,
    fetchedGroupStops,
  ]);

  const selectedIds = useMemo(
    () => new Set(stops.map((s) => s.exhibition.id)),
    [stops],
  );

  const availableExhibitions = useMemo(() => {
    return (itinerantList?.data ?? []).filter((e) => !selectedIds.has(e.id));
  }, [itinerantList, selectedIds]);

  const handleStopsReorder = (next: ItinerantStopDraft[]) => {
    userReorderedRef.current = true;
    onStopsChange(next);
  };

  const addExhibition = (exhibition: Exhibition) => {
    if (selectedIds.has(exhibition.id)) return;
    const newStop = itinerantStopFromExhibition(exhibition);
    const next = [...stops, newStop];
    // Default organization = by date; after a manual reorder, append only
    if (!userReorderedRef.current) {
      next.sort((a, b) => sortByDateDefault(a.exhibition, b.exhibition));
    }
    onStopsChange(next);
    onSelectStop?.(newStop.id);
  };

  const removeStop = (id: string) => {
    const remaining = stops.filter((s) => s.id !== id);
    onStopsChange(remaining);
    if (selectedStopId === id && remaining.length > 0) {
      onSelectStop?.(remaining[0].id);
    }
  };

  const updateStop = (
    id: string,
    patch: Partial<
      Pick<ItinerantStopDraft, "catalogNumber" | "exemplaireExpose" | "note">
    >,
  ) => {
    onStopsChange(stops.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  return (
    <div className="flex flex-col gap-4 min-h-0 h-full overflow-hidden border-r pr-4">
      <div className="flex flex-col gap-2 overflow-auto min-h-0 flex-1">
        <div className="flex flex-col gap-0.5 shrink-0">
          <h3 className="text-sm font-semibold text-gray-800">
            {t("entryexhibitions")} ({stops.length})
          </h3>
          <p className="text-primary text-[12px] font-normal font-['Raleway'] leading-normal">
            {vt(
              "bo.itinerantExhibition.stopsHint",
              "Réordonnez les étapes : l’ordre de la liste pilote l’affichage du résumé.",
            )}
          </p>
        </div>
        <Sortable
          value={stops}
          onValueChange={handleStopsReorder}
          orientation="vertical"
        >
          <div className="flex flex-col gap-3">
            {stops.map((stop) => {
              const isSelected = selectedStopId === stop.id;
              return (
                <SortableItem key={stop.id} value={stop.id}>
                  <div
                    role={onSelectStop ? "button" : undefined}
                    tabIndex={onSelectStop ? 0 : undefined}
                    onClick={() => onSelectStop?.(stop.id)}
                    onKeyDown={(e) => {
                      if (!onSelectStop) return;
                      const tag = (e.target as HTMLElement)?.tagName;
                      if (tag === "TEXTAREA" || tag === "INPUT") return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelectStop(stop.id);
                      }
                    }}
                    className={cn(
                      "border rounded-md p-3 bg-white flex gap-2",
                      onSelectStop && "cursor-pointer",
                      isSelected && "border-[#005D8D] ring-1 ring-[#005D8D]",
                    )}
                  >
                    <SortableDragHandle
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0 mt-1"
                      aria-label="Réordonner"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <GripVertical className="h-4 w-4 text-gray-400" />
                    </SortableDragHandle>
                    <div className="flex-1 min-w-0 flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs text-gray-500">
                            {stop.exhibition.expositionsDescriptionNumeroExpo}
                          </p>
                          <div
                            className="text-sm font-medium leading-snug break-words [&_i]:text-[length:inherit] [&_em]:text-[length:inherit] [&_i]:leading-[inherit] [&_em]:leading-[inherit] [&_p]:m-0 [&_p]:inline"
                            dangerouslySetInnerHTML={{
                              __html:
                                stop.exhibition.expositionsDescriptionResume ||
                                "",
                            }}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeStop(stop.id);
                          }}
                          disabled={stops.length <= 1}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                      {inlineFields && showCatalogNumber && (
                        <Input
                          placeholder={t(
                            "oeuvresDansExpositionsDescriptionNumeroCatalogue",
                          )}
                          value={stop.catalogNumber}
                          onChange={(e) =>
                            updateStop(stop.id, {
                              catalogNumber: e.target.value,
                            })
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      {inlineFields && showExemplaireExpose && (
                        <Input
                          placeholder={t(
                            "expositionsDansEstampesMultiplesDescriptionExemplaireExpose",
                          )}
                          value={stop.exemplaireExpose}
                          onChange={(e) =>
                            updateStop(stop.id, {
                              exemplaireExpose: e.target.value,
                            })
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      {/* Dedicated itinerant note (CF) — not the classic link notes */}
                      <Textarea
                        placeholder={vt(
                          "bo.itinerantExhibition.notePlaceholder",
                          "Note dédiée à cette étape",
                        )}
                        value={stop.note}
                        rows={2}
                        onChange={(e) =>
                          updateStop(stop.id, { note: e.target.value })
                        }
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                </SortableItem>
              );
            })}
          </div>
        </Sortable>
      </div>

      <div className="flex flex-col gap-2 min-h-0 flex-1 overflow-hidden border-t pt-3">
        <h3 className="text-sm font-semibold text-gray-800 shrink-0">
          {t("expositionsDescriptionExpositionItinerante")}
        </h3>
        <Input
          placeholder={t("search")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPagination((p) => ({ ...p, page: 0 }));
          }}
        />
        <div className="flex-1 overflow-auto flex flex-col gap-2 min-h-0">
          {availableExhibitions.map((exhibition) => (
            <div
              key={exhibition.id}
              className="border rounded-md p-3 flex items-center gap-2 justify-between"
            >
              <div className="min-w-0">
                <p className="text-xs text-gray-500">
                  {exhibition.expositionsDescriptionNumeroExpo}
                </p>
                <p
                  className="text-sm font-medium truncate"
                  dangerouslySetInnerHTML={{
                    __html:
                      exhibition.expositionsDescriptionTitre ||
                      exhibition.expositionsDescriptionResume ||
                      "",
                  }}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => addExhibition(exhibition)}
              >
                <Plus className="h-4 w-4 text-[#005D8D]" />
              </Button>
            </div>
          ))}
          {availableExhibitions.length === 0 && (
            <p className="text-sm text-gray-500 py-4">{t("noResults") || "—"}</p>
          )}
        </div>
        {(itinerantList?.totalSize ?? 0) >
          (pagination.page + 1) * pagination.pageSize && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
          >
            {t("loadMore") || "…"}
          </Button>
        )}
      </div>
    </div>
  );
}
